/**
 * The `/modules/$moduleCode` search contract, validated AT THE BOUNDARY the
 * way every other route's search is: the page below only ever sees a shape it
 * can render, and anything else is dropped rather than carried inward as a
 * string somebody later has to remember not to trust.
 *
 * The keys are deliberately MODULE-AGNOSTIC — a queue of module events is
 * filtered by lifecycle status, priority, assignee, received window, an
 * identity search and a Patient scope whatever the module is — and the VALUES
 * stay loosely-typed bounded strings here: which statuses exist is the
 * module's own vocabulary, so the module workspace resolves each value
 * against its closed set and treats an unknown one as unfiltered. This
 * validator's job is only shape and bound, so a hostile address cannot smuggle
 * an unbounded or non-string value into page state.
 */
export interface ModuleWorkspaceSearch {
  /**
   * WHICH OF THE WORKSPACE'S OWN SECTIONS IS ON SCREEN — a module whose
   * workspace is an AREA rather than one queue (OnDoc: the booking requests,
   * the exchange journal, a Patient's attachment, a Patient's documents) draws
   * its own navigation panel, and the section a tile leads to is part of the
   * address like every other view state here. The spelling is the module's own
   * closed vocabulary, resolved by the module against what this account may
   * see, so an unknown or withheld value lands on its default section rather
   * than on an empty page.
   */
  view?: string
  /** A retained document to inspect after a cross-surface handoff. */
  document?: string
  /**
   * THE PATIENT THE VIEW IS SCOPED TO, where a module's workspace holds
   * per-Patient surfaces — the chart is chosen on the page rather than by the
   * route, so without this the choice would be lost on every move between the
   * area's sections and could not be handed to a colleague. It is an opaque
   * Core id, exactly as `/patients/$patientId` already carries one.
   */
  patient?: string
  /** A comma-separated set of lifecycle values — a queue narrows to several at
   *  once, and an absent key is the module's own standing default, not "all". */
  status?: string
  priority?: string
  assigned?: string
  from?: string
  to?: string
  /** The identity criterion — the queue's one free-text question, which the
   *  module's contract resolves over Patient identity on the server. */
  q?: string
}

const BOUNDS: Record<keyof ModuleWorkspaceSearch, number> = {
  // One section name, not a list: a workspace shows one of its sections.
  view: 32,
  document: 64,
  patient: 64,
  // Wide enough for a module's whole lifecycle vocabulary listed at once.
  status: 160,
  priority: 32,
  assigned: 64,
  from: 10,
  to: 10,
  q: 160,
}

export function validateModuleWorkspaceSearch(
  search: Record<string, unknown>,
): ModuleWorkspaceSearch {
  const result: ModuleWorkspaceSearch = {}
  for (const key of Object.keys(BOUNDS) as (keyof ModuleWorkspaceSearch)[]) {
    const raw = search[key]
    if (typeof raw === 'string' && raw !== '' && raw.length <= BOUNDS[key]) {
      result[key] = raw
    }
  }
  return result
}
